import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextareaAutosize,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Message as MessageIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Send as SendIcon,
  History as HistoryIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  PushPin as PushPinIcon,
} from '@mui/icons-material';

interface NotificationTemplate {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'push' | 'in-app';
  subject: string;
  content: string;
  variables: string[];
  isActive: boolean;
  createdAt: string;
}

interface NotificationLog {
  id: string;
  recipient: string;
  type: string;
  subject: string;
  status: 'sent' | 'failed' | 'pending';
  sentAt: string;
  errorMessage?: string;
}

interface NotificationConfig {
  emailEnabled: boolean;
  smsEnabled: boolean;
  pushEnabled: boolean;
  inAppEnabled: boolean;
  defaultSender: string;
  retryAttempts: number;
  batchSize: number;
  autoCleanup: boolean;
}

const NotificationManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [notificationLogs, setNotificationLogs] = useState<NotificationLog[]>([]);
  const [notificationConfig, setNotificationConfig] = useState<NotificationConfig>({
    emailEnabled: true,
    smsEnabled: true,
    pushEnabled: false,
    inAppEnabled: true,
    defaultSender: 'Monio Notifications',
    retryAttempts: 3,
    batchSize: 100,
    autoCleanup: true,
  });
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadNotificationData();
  }, []);

  const loadNotificationData = async () => {
    try {
      // Load notification templates
      const templatesResponse = await fetch('/api/admin/notifications/templates');
      if (templatesResponse.ok) {
        const templatesData = await templatesResponse.json();
        setTemplates(templatesData);
      }

      // Load notification logs
      const logsResponse = await fetch('/api/admin/notifications/logs');
      if (logsResponse.ok) {
        const logsData = await logsResponse.json();
        setNotificationLogs(logsData);
      }

      // Load notification configuration
      const configResponse = await fetch('/api/admin/notifications/config');
      if (configResponse.ok) {
        const config = await configResponse.json();
        setNotificationConfig(config);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load notification data' });
    }
  };

  const handleConfigSave = async () => {
    try {
      const response = await fetch('/api/admin/notifications/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(notificationConfig),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Notification configuration saved successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save notification configuration' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving notification configuration' });
    }
  };

  const handleTemplateSave = async (template: NotificationTemplate) => {
    try {
      const response = await fetch('/api/admin/notifications/templates', {
        method: template.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(template),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Template saved successfully' });
        setIsTemplateDialogOpen(false);
        setEditingTemplate(null);
        loadNotificationData();
      } else {
        setMessage({ type: 'error', text: 'Failed to save template' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving template' });
    }
  };

  const handleTemplateDelete = async (templateId: string) => {
    try {
      const response = await fetch(`/api/admin/notifications/templates/${templateId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Template deleted successfully' });
        loadNotificationData();
      } else {
        setMessage({ type: 'error', text: 'Failed to delete template' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting template' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email': return <EmailIcon />;
      case 'sms': return <SmsIcon />;
      case 'push': return <PushPinIcon />;
      case 'in-app': return <NotificationsIcon />;
      default: return <MessageIcon />;
    }
  };

  const totalSent = notificationLogs.filter(log => log.status === 'sent').length;
  const totalFailed = notificationLogs.filter(log => log.status === 'failed').length;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
        <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Notification Management
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab icon={<SettingsIcon />} label="Configuration" />
          <Tab icon={<MessageIcon />} label="Templates" />
          <Tab icon={<HistoryIcon />} label="Notification Logs" />
          <Tab icon={<SendIcon />} label="Send Notifications" />
        </Tabs>
      </Paper>

      {/* Configuration Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notification Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Default Sender"
                      value={notificationConfig.defaultSender}
                      onChange={(e) => setNotificationConfig({ ...notificationConfig, defaultSender: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Retry Attempts"
                      type="number"
                      value={notificationConfig.retryAttempts}
                      onChange={(e) => setNotificationConfig({ ...notificationConfig, retryAttempts: parseInt(e.target.value) })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Batch Size"
                      type="number"
                      value={notificationConfig.batchSize}
                      onChange={(e) => setNotificationConfig({ ...notificationConfig, batchSize: parseInt(e.target.value) })}
                      margin="normal"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationConfig.emailEnabled}
                        onChange={(e) => setNotificationConfig({ ...notificationConfig, emailEnabled: e.target.checked })}
                      />
                    }
                    label="Enable Email Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationConfig.smsEnabled}
                        onChange={(e) => setNotificationConfig({ ...notificationConfig, smsEnabled: e.target.checked })}
                      />
                    }
                    label="Enable SMS Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationConfig.pushEnabled}
                        onChange={(e) => setNotificationConfig({ ...notificationConfig, pushEnabled: e.target.checked })}
                      />
                    }
                    label="Enable Push Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationConfig.inAppEnabled}
                        onChange={(e) => setNotificationConfig({ ...notificationConfig, inAppEnabled: e.target.checked })}
                      />
                    }
                    label="Enable In-App Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationConfig.autoCleanup}
                        onChange={(e) => setNotificationConfig({ ...notificationConfig, autoCleanup: e.target.checked })}
                      />
                    }
                    label="Auto Cleanup Old Logs"
                  />
                </Box>
                <Box sx={{ mt: 3 }}>
                  <Button variant="contained" onClick={handleConfigSave}>
                    Save Configuration
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notification Statistics
                </Typography>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="success.main">
                    {totalSent}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Notifications Sent
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="error">
                    {totalFailed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Failed Notifications
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Templates Tab */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Notification Templates</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingTemplate({
                  id: '',
                  name: '',
                  type: 'email',
                  subject: '',
                  content: '',
                  variables: [],
                  isActive: true,
                  createdAt: new Date().toISOString(),
                });
                setIsTemplateDialogOpen(true);
              }}
            >
              Add Template
            </Button>
          </Box>
          <Grid container spacing={2}>
            {templates.map((template) => (
              <Grid item xs={12} md={6} lg={4} key={template.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6" gutterBottom>
                        {template.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getTypeIcon(template.type)}
                        <Chip
                          label={template.type}
                          size="small"
                          color={template.isActive ? 'success' : 'default'}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {template.subject}
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {template.content.substring(0, 100)}...
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingTemplate(template);
                          setIsTemplateDialogOpen(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleTemplateDelete(template.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Notification Logs Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Notification Logs
            </Typography>
            <List>
              {notificationLogs.map((log) => (
                <ListItem key={log.id} divider>
                  <ListItemText
                    primary={log.subject}
                    secondary={
                      <Box>
                        <Typography variant="body2">To: {log.recipient}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Sent: {new Date(log.sentAt).toLocaleString()}
                        </Typography>
                        {log.errorMessage && (
                          <Typography variant="body2" color="error">
                            Error: {log.errorMessage}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTypeIcon(log.type)}
                      <Chip
                        label={log.status}
                        color={getStatusColor(log.status) as any}
                        size="small"
                      />
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Send Notifications Tab */}
      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Send Notifications
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Select Template</InputLabel>
                  <Select label="Select Template">
                    {templates.filter(t => t.isActive).map(template => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name} ({template.type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Recipients (comma-separated emails/phones)"
                  margin="normal"
                  multiline
                  rows={3}
                />
                <TextField
                  fullWidth
                  label="Subject (optional)"
                  margin="normal"
                />
                <TextareaAutosize
                  minRows={4}
                  placeholder="Message content (optional)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                    marginTop: '16px',
                  }}
                />
                <Box sx={{ mt: 2 }}>
                  <Button variant="contained" startIcon={<SendIcon />}>
                    Send Notification
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<EmailIcon />}
                    >
                      Send Welcome Email to All Users
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<SmsIcon />}
                    >
                      Send SMS Alert to Admins
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<NotificationsIcon />}
                    >
                      Send In-App Notification
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Template Dialog */}
      <Dialog open={isTemplateDialogOpen} onClose={() => setIsTemplateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTemplate?.id ? 'Edit Template' : 'Add Template'}
        </DialogTitle>
        <DialogContent>
          {editingTemplate && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Notification Type</InputLabel>
                  <Select
                    value={editingTemplate.type}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, type: e.target.value as any })}
                    label="Notification Type"
                  >
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="sms">SMS</MenuItem>
                    <MenuItem value="push">Push Notification</MenuItem>
                    <MenuItem value="in-app">In-App Notification</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subject"
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  Content
                </Typography>
                <TextareaAutosize
                  minRows={6}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                  }}
                  value={editingTemplate.content}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, content: e.target.value })}
                  placeholder="Enter notification content. Use {{variable}} for dynamic content."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingTemplate.isActive}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTemplateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => editingTemplate && handleTemplateSave(editingTemplate)}
            variant="contained"
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationManagement; 